<template>

  <div class="einvoice-detail-page">
     <KTCodePreview v-bind:title="pageTitle"
      > <template v-slot:toolbar
        > <b-row v-if="detail"
          > <!-- P0: New toolbar component with clear CTA hierarchy --> <EInvoiceDetailToolbar
            :invoice-no="detailViewModel.invoiceNo"
            :status="detailViewModel.status"
            :is-loading="einvoiceIsActionLoading || einvoiceIsDetailLoading"
            :has-blocking-errors="hasBlockingReadinessErrors"
            :issue-disabled-reason="issueDisabledReason"
            :can-edit="canEditAction"
            :can-issue="canIssueVisible"
            :can-download-pdf="canDownloadPdfAction"
            :can-validate-draft="canValidateDraftAction"
            :can-preview-draft="canPreviewDraftAction"
            :pdf-cooldown-active="isPdfCooldownActive"
            :pdf-cooldown-seconds="issuedPdfReadinessMeta.remainingSeconds"
            :can-refresh-tax="canRefreshTaxAction"
            :can-resend-email="canResendEmailAction"
            :can-adjust="canAdjustAction"
            :can-replace="canReplaceAction"
            :can-cancel="canCancelAction"
            :can-delete-draft="canDeleteDraftAction"
            @go-back="goBack"
            @edit="showEditModal"
            @issue="showIssueConfirm"
            @download-pdf="openPdfViewer"
            @validate-draft="validateDraft"
            @preview-draft="openDraftPreview"
            @refresh-tax="refreshTaxStatus"
            @resend-email="showResendEmailModal"
            @adjust="showAdjustModal"
            @replace="showReplaceModal"
            @cancel="showCancelModal"
            @delete-draft="showDeleteDraftConfirm"
          /> </b-row
        > </template
      > <template v-slot:preview
        > <!-- Loading skeleton --> <b-skeleton-wrapper :loading="einvoiceIsDetailLoading"
          > <template #loading
            > <b-row
              > <b-col md="8"
                > <b-skeleton
                  width="60%"
                  class="mb-3"
                ></b-skeleton
                > <b-skeleton-table
                  :rows="4"
                  :columns="2"
                  :table-props="{ bordered: true }"
                ></b-skeleton-table
                > </b-col
              > <b-col md="4"
                > <b-skeleton
                  width="40%"
                  class="mb-3"
                ></b-skeleton
                > <b-skeleton
                  width="100%"
                  class="mb-2"
                  v-for="n in 6"
                  :key="n"
                ></b-skeleton
                > </b-col
              > </b-row
            > </template
          > <template v-if="detail"
            >
            <div class="einvoice-support-rail mb-4">
               <!-- Status Timeline -->
              <div class="einvoice-support-rail__card mb-4">
                 <EInvoiceStatusTimeline :invoice="detail" />
              </div>
               <!-- Rejected feedback -->
              <div
                v-if="detail.status === STATUS.TAX_REJECTED && detail.feedbackContent"
                class="einvoice-readiness-banner einvoice-support-rail__alert rounded p-4 mb-4 bg-light-danger border border-danger border-opacity-25"
              >

                <div class="d-flex align-items-start">

                  <div
                    class="banner-icon mr-3 rounded-circle d-flex align-items-center justify-content-center bg-danger text-white"
                  >
                     <i class="fas fa-exclamation-triangle fa-lg"></i>
                  </div>

                  <div class="flex-grow-1">

                    <h6 class="font-weight-bolder text-danger mb-1">CQT từ chối</h6>

                    <p class="mb-0 text-muted">
                       Hóa đơn đã bị CQT từ chối cấp mã do nội dung phản hồi bên dưới.
                    </p>

                    <div class="mt-3 bg-white rounded p-3 border border-danger border-opacity-25">

                      <div class="small font-weight-bold text-danger text-uppercase mb-2">
                         Nội dung phản hồi
                      </div>

                      <p class="mb-0 text-dark">{{ detail.feedbackContent }}</p>

                    </div>

                  </div>

                </div>

              </div>
               <!-- Issue failed error -->
              <div
                v-if="detail.status === STATUS.ISSUE_FAILED"
                class="einvoice-readiness-banner einvoice-support-rail__alert rounded p-4 mb-4 bg-light-danger border border-danger border-opacity-25"
              >

                <div class="d-flex align-items-start">

                  <div
                    class="banner-icon mr-3 rounded-circle d-flex align-items-center justify-content-center bg-danger text-white"
                  >
                     <i class="fas fa-times-circle fa-lg"></i>
                  </div>

                  <div class="flex-grow-1">

                    <h6 class="font-weight-bolder text-danger mb-1">Phát hành lỗi</h6>

                    <p class="mb-0 text-muted">Có lỗi xảy ra trong quá trình phát hành hóa đơn.</p>

                    <div class="mt-3 bg-white rounded p-3 border border-danger border-opacity-25">

                      <div class="small font-weight-bold text-danger text-uppercase mb-2">
                         Chi tiết lỗi
                      </div>

                      <p class="mb-1 text-dark">{{ getReasonCodeName(detail.reasonCode) }}</p>
                       <small
                        v-if="detail.providerMessage"
                        class="text-muted d-block"
                        > FAST: {{ detail.providerMessage }} </small
                      >
                    </div>

                  </div>

                </div>

              </div>

              <div
                v-if="lifecycleMeta.showAlert"
                class="einvoice-readiness-banner einvoice-support-rail__alert rounded p-4 mb-4"
                :class="`bg-light-${lifecycleMeta.variant} border border-${lifecycleMeta.variant} border-opacity-25`"
              >

                <div class="d-flex align-items-start">

                  <div
                    class="banner-icon mr-3 rounded-circle d-flex align-items-center justify-content-center text-white"
                    :class="`bg-${lifecycleMeta.variant}`"
                  >
                     <i :class="`${lifecycleMeta.icon} fa-lg`"></i>
                  </div>

                  <div class="flex-grow-1">

                    <h6
                      class="font-weight-bolder mb-1"
                      :class="`text-${lifecycleMeta.variant}`"
                    >
                       {{ lifecycleMeta.title }}
                    </h6>

                    <p class="mb-0 text-muted">{{ lifecycleMeta.message }}</p>

                    <div
                      v-if="lifecycleMeta.suggestedActionLabels.length"
                      class="mt-3"
                    >

                      <div class="small font-weight-bold text-uppercase text-muted mb-2">
                         Hành động hợp lệ hiện tại
                      </div>

                      <div class="d-flex flex-wrap gap-2">
                         <b-badge
                          v-for="label in lifecycleMeta.suggestedActionLabels"
                          :key="label"
                          :variant="lifecycleMeta.variant"
                          class="mr-2 mb-2 p-2 px-3 badge-pill"
                          > {{ label }} </b-badge
                        >
                      </div>

                    </div>

                  </div>

                </div>

              </div>

              <div
                v-if="contractBlockerBanner"
                class="einvoice-readiness-banner einvoice-support-rail__alert rounded p-4 mb-4"
                :class="`bg-light-${contractBlockerBanner.variant} border border-${contractBlockerBanner.variant} border-opacity-25`"
              >

                <div class="d-flex align-items-start">

                  <div
                    class="banner-icon mr-3 rounded-circle d-flex align-items-center justify-content-center text-white"
                    :class="`bg-${contractBlockerBanner.variant}`"
                  >
                     <i :class="`${contractBlockerBanner.icon} fa-lg`"></i>
                  </div>

                  <div class="flex-grow-1">

                    <h6
                      class="font-weight-bolder mb-1"
                      :class="`text-${contractBlockerBanner.variant}`"
                    >
                       {{ contractBlockerBanner.title }}
                    </h6>

                    <p class="mb-0 text-muted">{{ contractBlockerBanner.message }}</p>
                     <small
                      v-if="contractBlockerBanner.hint"
                      class="d-block mt-2 text-muted"
                      >{{ contractBlockerBanner.hint }}</small
                    >
                  </div>

                </div>

              </div>

            </div>
             <b-row
              > <!-- Left: Invoice Info --> <b-col
                > <!-- Main info card -->
                <div class="mb-4">

                  <div class="einvoice-detail-document">

                    <section class="einvoice-document-masthead">

                      <div class="einvoice-document-masthead__identity">

                        <div class="einvoice-document-kicker">Hoá đơn điện tử</div>

                        <h5 class="einvoice-document-title mb-2">Hoá đơn giá trị gia tăng</h5>

                        <div class="einvoice-document-subtitle">
                           Chứng từ điện tử theo chuẩn GTGT
                        </div>

                        <div class="einvoice-document-lead-meta">

                          <div class="einvoice-document-lead-row">
                             <span class="einvoice-document-lead-label">Ngày hoá đơn</span> <span
                              class="einvoice-document-lead-value"
                              > {{ formatInvoiceDateShort(detailViewModel.invoiceDate) }} </span
                            >
                          </div>

                          <div class="einvoice-document-lead-row">
                             <span class="einvoice-document-lead-label">Số bill</span> <span
                              class="einvoice-document-lead-value"
                              :class="getFieldDisplayClass('billNumber')"
                              > {{ getFieldDisplayText('billNumber') }} </span
                            >
                          </div>

                          <div class="einvoice-document-lead-row">
                             <span class="einvoice-document-lead-label">Loại hóa đơn</span> <span
                              class="einvoice-document-lead-value"
                              > {{ detailViewModel.billTypeName }} </span
                            >
                          </div>

                          <div v-if="detail.codeShip" class="einvoice-document-lead-row">
                             <span class="einvoice-document-lead-label">Mã vận đơn</span> <span
                              class="einvoice-document-lead-value"
                              > {{ detail.codeShip }} </span
                            >
                          </div>

                        </div>

                      </div>

                      <div class="einvoice-document-masthead__facts">

                        <div class="einvoice-document-fact-group">

                          <div class="einvoice-fact-label">Số hoá đơn</div>

                          <div
                            class="einvoice-fact-value einvoice-fact-value--invoice"
                            :class="getFieldDisplayClass('invoiceNo')"
                          >
                             {{ getFieldDisplayText('invoiceNo') }}
                          </div>

                        </div>

                        <div class="einvoice-document-fact-grid">

                          <div class="einvoice-document-fact-item">

                            <div class="einvoice-fact-label">Mẫu số</div>

                            <div
                              class="einvoice-fact-value"
                              :class="getFieldDisplayClass('pattern')"
                            >
                               {{ getFieldDisplayText('pattern') }}
                            </div>

                          </div>

                          <div class="einvoice-document-fact-item">

                            <div class="einvoice-fact-label">Ký hiệu</div>

                            <div
                              class="einvoice-fact-value"
                              :class="getFieldDisplayClass('serial')"
                            >
                               {{ getFieldDisplayText('serial') }}
                            </div>

                          </div>

                          <div
                            class="einvoice-document-fact-item einvoice-document-fact-item--status"
                          >

                            <div class="einvoice-fact-label">Trạng thái</div>

                            <div class="einvoice-fact-value einvoice-fact-value--status">
                               <EInvoiceStatusBadge :status="detail.status" />
                              <EinvoiceAdjustmentDraftBadge
                                v-if="detail.adjustmentDraft && getAdjustmentDraftDetailHref(detail.adjustmentDraft)"
                                :status="detail.adjustmentDraft.status"
                                linkable
                                :href="getAdjustmentDraftDetailHref(detail.adjustmentDraft)"
                                link-title="Mở chi tiết nháp điều chỉnh (tab mới)"
                                wrapper-class="ml-2"
                                @link-click="openAdjustmentDraftDetail"
                              />
                            </div>

                          </div>

                        </div>

                      </div>

                    </section>

                    <section class="einvoice-document-parties">

                      <div class="einvoice-party-block">

                        <div class="einvoice-party-heading">

                          <div class="einvoice-party-label">Người mua</div>

                          <div class="einvoice-party-title">
                             {{ detailViewModel.buyerName || 'Khách lẻ' }}
                          </div>

                        </div>

                        <div class="einvoice-party-grid">

                          <div class="einvoice-party-row">

                            <div class="einvoice-party-row__label">Mã số thuế</div>

                            <div
                              class="einvoice-party-row__value"
                              :class="getFieldDisplayClass('buyerTaxCode')"
                            >
                               {{ getFieldDisplayText('buyerTaxCode') }}
                            </div>

                          </div>

                          <div class="einvoice-party-row">

                            <div class="einvoice-party-row__label">Địa chỉ</div>

                            <div
                              class="einvoice-party-row__value"
                              :class="getFieldDisplayClass('buyerAddress')"
                            >
                               {{ getFieldDisplayText('buyerAddress') }}
                            </div>

                          </div>

                          <div class="einvoice-party-row">

                            <div class="einvoice-party-row__label">Email</div>

                            <div
                              class="einvoice-party-row__value"
                              :class="getFieldDisplayClass('buyerEmail')"
                            >
                               {{ getFieldDisplayText('buyerEmail') }}
                            </div>

                          </div>

                          <div class="einvoice-party-row">

                            <div class="einvoice-party-row__label">Số điện thoại</div>

                            <div
                              class="einvoice-party-row__value"
                              :class="getFieldDisplayClass('buyerPhone')"
                            >
                               {{ getFieldDisplayText('buyerPhone') }}
                            </div>

                          </div>

                          <div class="einvoice-party-row">

                            <div class="einvoice-party-row__label">CCCD/CMND</div>

                            <div
                              class="einvoice-party-row__value"
                              :class="getFieldDisplayClass('buyerIdCard')"
                            >
                               {{ getFieldDisplayText('buyerIdCard') }}
                            </div>

                          </div>

                        </div>

                      </div>

                    </section>

                    <section class="einvoice-document-body">
                       <EInvoiceVatTables
                        :items="detailViewModel.lineItems"
                        :summary="detailViewModel.summary"
                        :amount-in-words-text="getFieldDisplayText('amountInWords')"
                        :amount-in-words-class="getFieldDisplayClass('amountInWords')"
                        detail-mode
                        show-product-image
                      />
                    </section>

                  </div>

                </div>
                 <!-- Related invoices (lineage) -->
                <div
                  v-if="detailViewModel.relations && detailViewModel.relations.length"
                  class="mb-4"
                >

                  <div class="einvoice-traceability-head mb-3 mt-5">

                    <div class="einvoice-traceability-head__eyebrow">Traceability</div>

                    <h5 class="mb-0 einvoice-section-title font-weight-bolder">
                       Hoá đơn liên quan
                    </h5>

                  </div>

                  <div class="einvoice-info-card p-0 overflow-hidden">

                    <div class="table-responsive">

                      <table class="table table-bordered-list table-hover mb-0">

                        <caption class="sr-only">
                           Danh sách hoá đơn điện tử liên quan
                        </caption>

                        <thead>

                          <tr>

                            <th
                              scope="col"
                              class="text-center"
                              style="width: 120px"
                            >
                               Loại
                            </th>

                            <th scope="col">Số HĐ liên quan</th>

                            <th
                              scope="col"
                              class="text-center"
                            >
                               Ngày
                            </th>

                          </tr>

                        </thead>

                        <tbody>

                          <tr
                            v-for="(item, index) in detailViewModel.relations"
                            :key="item.id || item.relatedInvoiceId || index"
                          >

                            <td class="text-center">
                               <b-badge :variant="getRelationVariant(item.relationType)"
                                > {{ getRelationLabel(item.relationType) }} </b-badge
                              >
                            </td>

                            <td>
                               <router-link
                                :to="{
                                  name: 'einvoice-detail',
                                  params: {
                                    salesInvoiceId: item.salesInvoiceId || item.relatedInvoiceId,
                                  },
                                  query: { saleType: detailViewModel.saleType },
                                }"
                                class="text-primary"
                                > {{ item.invoiceNo || '---' }} </router-link
                              >
                            </td>

                            <td class="text-center">
                               {{ formatInvoiceDateShort(item.invoiceDate) }}
                            </td>

                          </tr>

                        </tbody>

                      </table>

                    </div>

                  </div>

                </div>
                 </b-col
              > </b-row
            > </template
          > </b-skeleton-wrapper
        > <!-- Modals --> <EInvoiceEditModal
          ref="editModal"
          :invoice="draftEditorSeed"
          @updated="onActionComplete"
          @issued="onActionComplete"
        /> <EInvoiceCancelModal
          ref="cancelModal"
          :invoice="actionSubject"
          @canceled="onActionComplete"
        /> <EInvoiceAdjustModal
          ref="adjustModal"
          :invoice="actionSubject"
          @adjusted="onActionComplete"
        /> <EInvoiceReplaceModal
          ref="replaceModal"
          :invoice="actionSubject"
          @replaced="onActionComplete"
        /> <EInvoiceResendEmailModal
          ref="resendEmailModal"
          :invoice="actionSubject"
          @sent="onActionComplete"
        /> <PdfViewerModal ref="pdfViewer" /> </template
      > </KTCodePreview
    >
  </div>

</template>

<script>
  import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module'
  import {
    EINVOICE_FETCH_DETAIL,
    EINVOICE_DELETE_DRAFT,
    EINVOICE_ISSUE,
    EINVOICE_REFRESH_TAX,
    EINVOICE_VALIDATE_DRAFT,
  } from '@/core/services/store/modules/einvoice/einvoice.module'
  import {
    EINVOICE_RELATION_TYPE,
    EINVOICE_SALE_TYPE,
    EINVOICE_STATUS,
  } from '@/utils/einvoice.enum'
  import { getToastInstance } from '@/utils/toastHelper'
  import KTCodePreview from '@/view/content/CodePreview.vue'
  import Swal from 'sweetalert2'
  import EInvoiceAdjustModal from './components/EInvoiceAdjustModal.vue'
  import EInvoiceCancelModal from './components/EInvoiceCancelModal.vue'
  import EInvoiceDetailToolbar from './components/EInvoiceDetailToolbar.vue'
  import EInvoiceEditModal from './components/EInvoiceEditModal.vue'
  import EInvoiceReplaceModal from './components/EInvoiceReplaceModal.vue'
  import EInvoiceResendEmailModal from './components/EInvoiceResendEmailModal.vue'
  import EInvoiceStatusBadge from './components/EInvoiceStatusBadge.vue'
  import EinvoiceAdjustmentDraftBadge from './components/EinvoiceAdjustmentDraftBadge.vue'
  import EInvoiceStatusTimeline from './components/EInvoiceStatusTimeline.vue'
  import EInvoiceVatTables from './components/EInvoiceVatTables.vue'
  import PdfViewerModal from '@/view/components/pdf-viewer/PdfViewerModal.vue'
  import einvoiceMixin from './mixins/einvoice.mixin'
  import {
    buildArtifactDisplay,
    buildConfigBlockerMeta,
    buildDetailDisplay,
    buildDetailReadiness,
    getDisplayClass,
  } from './utils/detailDisplay.utils'
  import {
    buildActionSubject,
    buildDetailViewModel,
    buildDraftEditorSeed,
  } from './utils/einvoiceViewModel.selectors'
  import { buildLifecycleUiMeta } from './utils/lifecycleState.utils'
  import { resolveInvoiceIssuedPdfReadiness } from './utils/pdfReadiness.utils'
  import { buildEInvoicePdfViewerConfig } from './utils/pdfViewer.utils'
  import { buildEditDraftPayload } from './utils/editDraft.utils'
  // Banner variant config for DRY rendering
  const BANNER_CONFIGS = {
    TAX_REJECTED: {
      variant: 'danger',
      icon: 'fas fa-exclamation-triangle',
      title: 'CQT từ chối',
      message: 'Hóa đơn đã bị CQT từ chối cấp mã do nội dung phản hồi bên dưới.',
      detailLabel: 'Nội dung phản hồi',
    },
    ISSUE_FAILED: {
      variant: 'danger',
      icon: 'fas fa-times-circle',
      title: 'Phát hành lỗi',
      message: 'Có lỗi xảy ra trong quá trình phát hành hóa đơn.',
      detailLabel: 'Chi tiết lỗi',
    },
    CONFIG_BLOCKED: {
      variant: 'danger',
      icon: 'fas fa-tools',
    },
  }

  const { makeToastSuccess, makeToastFailure } = getToastInstance()

  export default {
    name: 'EInvoiceDetail',
    components: {
      KTCodePreview,
      EInvoiceStatusBadge,
      EinvoiceAdjustmentDraftBadge,
      EInvoiceStatusTimeline,
      EInvoiceDetailToolbar,
      EInvoiceEditModal,
      EInvoiceCancelModal,
      EInvoiceAdjustModal,
      EInvoiceReplaceModal,
      EInvoiceResendEmailModal,
      EInvoiceVatTables,
      PdfViewerModal,
    },
    mixins: [einvoiceMixin],
    data() {
      return {
        STATUS: EINVOICE_STATUS,
        BANNER_CONFIGS,
        pdfCooldownTickMs: Date.now(),
        pdfCooldownTimer: null,
      }
    },
    computed: {
      detail() {
        return this.einvoiceDetail
      },
      currentSalesInvoiceRouteKey() {
        return this.$route.params.salesInvoiceId
      },
      currentSaleTypeRouteKey() {
        return this.$route.query.saleType || null
      },
      detailViewModel() {
        return buildDetailViewModel(this.detail || {})
      },
      actionSubject() {
        return buildActionSubject(this.detail || {})
      },
      draftEditorSeed() {
        return buildDraftEditorSeed(this.detail || {})
      },
      pageTitle() {
        if (!this.detail) return 'Chi tiết hoá đơn điện tử'
        const billTypeName = this.detailViewModel.billTypeName || 'Không xác định'
        return `HĐĐT ${this.detailViewModel.invoiceNo || 'Nháp'} - ${billTypeName} #${this.detailViewModel.billNumber ?? '---'}`
      },
      hasIssuePermission() {
        if (!this.detail) return false
        return this.detail.saleType === EINVOICE_SALE_TYPE.RETAIL
          ? this.canIssueRetail()
          : this.canIssueWholesale()
      },
      hasEditPermission() {
        if (!this.detail) return false
        return this.canEditInvoice(this.detail.saleType)
      },
      isContractInvalid() {
        return this.detail?.contractInvalid === true
      },
      canEditAction() {
        if (this.isContractInvalid) return false
        return this.detail && this.canEditInvoiceByContext(this.detail)
      },
      canIssueVisible() {
        if (this.isContractInvalid) return false
        return this.detail && this.canIssueInvoiceByContext(this.detail)
      },
      hasSecondaryActions() {
        return (
          this.canRefreshTaxAction ||
          this.canDownloadPdfAction ||
          this.canResendEmailAction ||
          this.canShowCorrectionActions
        )
      },
      detailReadiness() {
        return buildDetailReadiness(this.detailViewModel || {})
      },
      detailUiDisplay() {
        return buildDetailDisplay(this.detailViewModel || {})
      },
      configBlockerMeta() {
        return buildConfigBlockerMeta(this.detail || {})
      },
      contractBlockerBanner() {
        // Check contractInvalid with BILL_MODE_UNSUPPORTED blocking reason first
        if (this.detail?.contractInvalid) {
          const blockingReasons = Array.isArray(this.detail?.blockingReasons)
            ? this.detail.blockingReasons
            : Array.isArray(this.detail?.actionContext?.blockingReasons)
              ? this.detail.actionContext.blockingReasons
              : []
          const hasBillModeUnsupported = blockingReasons.some(
            (r) =>
              (r?.code || r?.reasonCode || '').toString().trim().toUpperCase() ===
              'BILL_MODE_UNSUPPORTED',
          )
          if (hasBillModeUnsupported) {
            return {
              variant: 'danger',
              icon: 'fas fa-ban',
              title: 'Mode hóa đơn giao hàng chưa hỗ trợ',
              message:
                this.detail?.feedbackContent ||
                'Loại hóa đơn này chưa được hỗ trợ trong hệ thống. Không thể thực hiện thao tác.',
              hint: 'Không tự retry hay điều hướng. Cần backend hỗ trợ loại hóa đơn này.',
            }
          }

          return {
            variant: 'danger',
            icon: 'fas fa-exclamation-circle',
            title: 'Loại hóa đơn không hợp lệ',
            message:
              this.detail?.feedbackContent ||
              'Contract của hóa đơn này không hợp lệ. Không thể thực hiện thao tác.',
            hint: null,
          }
        }

        const code = String(this.detail?.blockedContractCode || '')
          .trim()
          .toUpperCase()
        if (!code) return null

        if (code === 'CONTRACT_GONE') {
          return {
            variant: 'danger',
            icon: 'fas fa-code-branch',
            title: 'Frontend đang dùng contract cũ',
            message:
              this.detail?.feedbackContent ||
              'Backend đã hard-cut route cũ. FE phải refresh để dùng canonical route mới.',
            hint: 'Không retry tự động. Đây là lỗi migration của FE.',
          }
        }

        if (code === 'EINVOICE_SNAPSHOT_MISSING') {
          return {
            variant: 'danger',
            icon: 'fas fa-layer-group',
            title: 'Snapshot HĐĐT chưa sẵn sàng',
            message:
              this.detail?.feedbackContent ||
              'Canonical snapshot đang thiếu nên FE buộc phải block thao tác.',
            hint: 'Không issue/preview/retry tự động. Cần backend backfill trước.',
          }
        }

        if (code === 'EINVOICE_SNAPSHOT_REPAIR_REQUIRED') {
          return {
            variant: 'danger',
            icon: 'fas fa-tools',
            title: 'Snapshot HĐĐT cần repair',
            message:
              this.detail?.feedbackContent ||
              'Canonical snapshot đang ở trạng thái repair_required nên FE phải block thao tác.',
            hint: 'Không tự hòa giải dữ liệu. Cần backend repair/backfill.',
          }
        }

        return null
      },
      artifactDisplay() {
        return buildArtifactDisplay(this.detail || {})
      },
      lifecycleMeta() {
        return buildLifecycleUiMeta(this.detail || {})
      },
      readinessBannerData() {
        const readiness = this.detailReadiness || {
          blockingErrors: [],
          pendingGeneration: [],
          warnings: [],
        }
        const blockingErrors = Array.isArray(readiness.blockingErrors)
          ? [...readiness.blockingErrors]
          : []
        const warnings = Array.isArray(readiness.warnings) ? [...readiness.warnings] : []

        if (
          this.configBlockerMeta.isBlocked &&
          !blockingErrors.some((item) => item?.message === this.configBlockerMeta.message)
        ) {
          blockingErrors.push({
            code: 'CONFIG_BLOCKER',
            message: this.configBlockerMeta.message,
          })
        }

        if (this.configBlockerMeta.isBlocked && this.configBlockerMeta.howToFix) {
          warnings.push(`Cách xử lý: ${this.configBlockerMeta.howToFix}`)
        }

        return {
          ...readiness,
          blockingErrors,
          warnings,
        }
      },
      hasBlockingReadinessErrors() {
        if (!this.detail) return false
        return (
          this.hasIssueBlockingReadinessByContext(this.detail) ||
          this.readinessBannerData.blockingErrors.length > 0
        )
      },
      issueDisabledReason() {
        if (this.einvoiceIsActionLoading) {
          return 'Đang xử lý thao tác'
        }

        if (!this.canIssueVisible) {
          return (
            this.getIssueDisabledReasonByContext(this.detail) ||
            'Hoá đơn chưa đủ điều kiện phát hành'
          )
        }

        if (!this.hasBlockingReadinessErrors) {
          return ''
        }
        const guardReason = this.getIssueDisabledReasonByContext(this.detail)
        if (guardReason) return guardReason

        return this.readinessBannerData.blockingErrors.map((item) => item.message).join('; ')
      },
      canRefreshTaxAction() {
        if (this.isContractInvalid) return false
        return this.detail && this.canRefreshTaxByContext(this.detail)
      },
      canDownloadPdfAction() {
        if (this.isContractInvalid) return false
        return this.detail && this.canDownloadPdfByContext(this.detail)
      },
      canValidateDraftAction() {
        if (this.isContractInvalid) return false
        return this.detail && this.canValidateDraftByContext(this.detail)
      },
      canPreviewDraftAction() {
        if (this.isContractInvalid) return false
        return this.detail && this.canPreviewDraftByContext(this.detail)
      },
      issuedPdfReadinessMeta() {
        return resolveInvoiceIssuedPdfReadiness(this.detail || {}, this.pdfCooldownTickMs)
      },
      isPdfCooldownActive() {
        return this.issuedPdfReadinessMeta.isCooldownActive
      },
      canResendEmailAction() {
        if (!this.detail) return false
        if (this.isContractInvalid) return false
        return (
          this.canResendEmailByContext(this.detail) &&
          this.checkPermission('EINVOICE_WHOLESALE_EMAIL')
        )
      },
      canShowCorrectionActions() {
        return this.canCancelAction || this.canAdjustAction || this.canReplaceAction
      },
      canCancelAction() {
        if (this.isContractInvalid) return false
        return this.detail && this.canCancelByContext(this.detail)
      },
      canAdjustAction() {
        if (this.isContractInvalid) return false
        return this.detail && this.canAdjustByContext(this.detail)
      },
      canReplaceAction() {
        if (this.isContractInvalid) return false
        return this.detail && this.canReplaceByContext(this.detail)
      },
      canDeleteDraftAction() {
        if (this.isContractInvalid) return false
        return this.detail && this.canDeleteDraftByContext(this.detail)
      },
    },
    watch: {
      '$route.params.salesInvoiceId'(newSalesInvoiceId) {
        if (newSalesInvoiceId && this.currentSaleTypeRouteKey) {
          this.$store.dispatch(EINVOICE_FETCH_DETAIL, {
            salesInvoiceId: newSalesInvoiceId,
            saleType: this.currentSaleTypeRouteKey,
          })
        }
      },
      '$route.query.saleType'(newSaleType) {
        if (this.currentSalesInvoiceRouteKey && newSaleType) {
          this.$store.dispatch(EINVOICE_FETCH_DETAIL, {
            salesInvoiceId: this.currentSalesInvoiceRouteKey,
            saleType: newSaleType,
          })
        }
      },
      detail: {
        immediate: false,
        handler(newDetail) {
          if (!newDetail?.salesInvoiceId || !this.currentSalesInvoiceRouteKey) return

          if (String(this.currentSalesInvoiceRouteKey) !== String(newDetail.salesInvoiceId)) {
            this.$router.replace({
              name: 'einvoice-detail',
              params: { salesInvoiceId: newDetail.salesInvoiceId },
              query: { saleType: newDetail.saleType },
            })
          }
        },
      },
    },
    created() {
      this.$store.dispatch(SET_BREADCRUMB, [
        { title: 'Hoá đơn điện tử', route: '/einvoices' },
        { title: 'Chi tiết' },
      ])
      this.startPdfCooldownTicker()
      const salesInvoiceId = this.$route.params.salesInvoiceId
      const saleType = this.$route.query.saleType
      if (salesInvoiceId) {
        this.$store.dispatch(EINVOICE_FETCH_DETAIL, { salesInvoiceId, saleType })
      }
    },
    beforeDestroy() {
      this.stopPdfCooldownTicker()
    },
    methods: {
      startPdfCooldownTicker() {
        this.stopPdfCooldownTicker()
        this.pdfCooldownTimer = setInterval(() => {
          this.pdfCooldownTickMs = Date.now()
        }, 1000)
      },
      stopPdfCooldownTicker() {
        if (this.pdfCooldownTimer) {
          clearInterval(this.pdfCooldownTimer)
        }
        this.pdfCooldownTimer = null
      },
      goBack() {
        this.$router.push({ name: 'einvoice-list' })
      },
      showEditModal() {
        this.$refs.editModal.show()
      },
      async showIssueConfirm() {
        if (!this.canIssueVisible || this.hasBlockingReadinessErrors) {
          makeToastFailure(this.issueDisabledReason)
          return
        }

        const result = await Swal.fire({
          title: 'Phát hành hoá đơn?',
          text: 'Hoá đơn sẽ được gửi lên FAST để phát hành và truyền lên CQT. Hành động này không thể hoàn tác.',
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#3699FF',
          cancelButtonColor: '#E5EAEE',
          confirmButtonText: 'Phát hành',
          cancelButtonText: '<span style="color:#464E5F">Huỷ</span>',
          reverseButtons: true,
        })
        if (result.isConfirmed) {
          this.issueInvoice()
        }
      },
      async issueInvoice() {
        try {
          if (!this.canIssueVisible || this.hasBlockingReadinessErrors) {
            makeToastFailure(this.issueDisabledReason)
            return
          }
          const context = this.resolveCanonicalActionContext()
          if (!context) return

          await this.$store.dispatch(EINVOICE_ISSUE, {
            ...context,
            signMode: 'HSM',
          })
          makeToastSuccess('Phát hành hoá đơn thành công')
        } catch (error) {
          makeToastFailure(
            error?.message ||
              (this.configBlockerMeta.isBlocked
                ? this.configBlockerMeta.message
                : 'Phát hành hoá đơn thất bại'),
          )
        }
      },
      async refreshTaxStatus() {
        try {
          const context = this.resolveCanonicalActionContext()
          if (!context) return

          await this.$store.dispatch(EINVOICE_REFRESH_TAX, {
            ...context,
          })
          makeToastSuccess('Đã cập nhật trạng thái CQT')
        } catch (error) {
          makeToastFailure(error?.message || 'Kiểm tra CQT thất bại')
        }
      },
      async validateDraft() {
        if (!this.canValidateDraftAction) {
          makeToastFailure('Backend không cho phép kiểm tra dữ liệu nháp hiện tại')
          return
        }

        try {
          const context = this.resolveCanonicalActionContext()
          if (!context) return

          const result = await this.$store.dispatch(EINVOICE_VALIDATE_DRAFT, {
            ...context,
            payload: buildEditDraftPayload(this.draftEditorSeed),
          })

          if (result?.isValid === false) {
            makeToastFailure(this.formatDraftValidationMessage(result))
            return
          }

          makeToastSuccess('Nháp HĐĐT hợp lệ')
        } catch (error) {
          makeToastFailure(error?.message || 'Kiểm tra dữ liệu nháp thất bại')
        }
      },
      formatDraftValidationMessage(result = {}) {
        const errors = Array.isArray(result.errors) ? result.errors : []
        const blockingErrors = Array.isArray(result?.readiness?.blockingErrors)
          ? result.readiness.blockingErrors
          : []
        const messages = [...errors, ...blockingErrors]
          .map((item) => item?.message || item)
          .map((item) => String(item || '').trim())
          .filter(Boolean)

        return messages.length ? messages.join('; ') : result.message || 'Nháp HĐĐT chưa hợp lệ'
      },
      async openDraftPreview() {
        if (!this.canPreviewDraftAction) {
          makeToastFailure('Backend không cho phép xem trước nháp hiện tại')
          return
        }

        await this.openPdfViewer()
      },
      async openPdfViewer() {
        if (this.isPdfCooldownActive) {
          const remainingSeconds = Math.max(1, this.issuedPdfReadinessMeta.remainingSeconds || 0)
          makeToastFailure(`PDF đang được chuẩn bị. Vui lòng thử lại sau ${remainingSeconds} giây.`)
          return
        }

        try {
          await this.$refs.pdfViewer.open(buildEInvoicePdfViewerConfig(this.$store, this.detail))
        } catch (error) {
          makeToastFailure(
            error?.message ||
              (this.configBlockerMeta.isBlocked
                ? this.configBlockerMeta.message
                : 'Không thể mở PDF'),
          )
        }
      },
      async showDeleteDraftConfirm() {
        if (!this.canDeleteDraftAction) {
          makeToastFailure('Backend không cho phép xoá nháp hiện tại')
          return
        }

        const result = await Swal.fire({
          title: 'Xoá nháp HĐĐT?',
          text: 'Bản nháp hiện tại sẽ bị xoá và không còn xuất hiện như một HĐĐT active.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#F64E60',
          cancelButtonColor: '#E5EAEE',
          confirmButtonText: 'Xoá nháp',
          cancelButtonText: '<span style="color:#464E5F">Huỷ</span>',
          reverseButtons: true,
        })

        if (result.isConfirmed) {
          await this.deleteDraft()
        }
      },
      async deleteDraft() {
        try {
          const context = this.resolveCanonicalActionContext()
          if (!context) return

          await this.$store.dispatch(EINVOICE_DELETE_DRAFT, {
            ...context,
          })
          makeToastSuccess('Đã xoá bản nháp thành công')
          this.goBack()
        } catch (error) {
          makeToastFailure(error?.message || 'Xoá bản nháp thất bại')
        }
      },
      showCancelModal() {
        this.$refs.cancelModal.show()
      },
      showAdjustModal() {
        this.$refs.adjustModal.show()
      },
      showReplaceModal() {
        this.$refs.replaceModal.show()
      },
      showResendEmailModal() {
        this.$refs.resendEmailModal.show()
      },
      onActionComplete() {
        const context = this.resolveCanonicalActionContext({ notify: false })
        if (!context) return

        this.$store.dispatch(EINVOICE_FETCH_DETAIL, {
          ...context,
        })
      },
      resolveCanonicalActionContext({ notify = true } = {}) {
        const salesInvoiceId = String(
          this.detail?.salesInvoiceId || this.currentSalesInvoiceRouteKey || '',
        ).trim()
        const saleType = String(this.detail?.saleType || this.currentSaleTypeRouteKey || '').trim()

        if (salesInvoiceId && saleType) {
          return {
            salesInvoiceId,
            saleType,
          }
        }

        if (notify) {
          makeToastFailure(
            'Thiếu salesInvoiceId/saleType theo contract canonical. Vui lòng tải lại trang và thử lại.',
          )
        }

        return null
      },
      getRelationVariant(type) {
        const map = {
          [EINVOICE_RELATION_TYPE.ADJUST]: 'info',
          [EINVOICE_RELATION_TYPE.REPLACE]: 'warning',
          [EINVOICE_RELATION_TYPE.CANCEL]: 'danger',
        }
        return map[type] || 'secondary'
      },
      getRelationLabel(type) {
        const map = {
          [EINVOICE_RELATION_TYPE.ADJUST]: 'Điều chỉnh',
          [EINVOICE_RELATION_TYPE.REPLACE]: 'Thay thế',
          [EINVOICE_RELATION_TYPE.CANCEL]: 'Huỷ',
        }
        return map[type] || type
      },
      getFieldDisplayText(field) {
        return this.detailUiDisplay[field]?.text || ''
      },
      getFieldDisplayClass(field) {
        return getDisplayClass(this.detailUiDisplay[field])
      },

      getAdjustmentDraftDetailHref(draft) {
        if (draft?.id == null) return null
        const { href } = this.$router.resolve({
          name: 'adjustment-draft-detail',
          params: { draftId: String(draft.id) },
        })
        return href || null
      },

      openAdjustmentDraftDetail(event) {
        event?.stopPropagation?.()
      },

    },
  }
</script>

<style lang="scss" scoped>
  @import './einvoice.scss';

.einvoice-detail-page {
  .einvoice-support-rail {
    display: grid;
    gap: 0.9rem;
  }

  .einvoice-support-rail__card {
    padding: 1rem 1.1rem;
    border: 1px solid #dde5ee;
    border-radius: 0.875rem;
    background: #fbfcfe;
  }

  .einvoice-support-rail__alert {
    margin-bottom: 0 !important;
    border-radius: 0.875rem !important;
  }

  .einvoice-detail-document {
    border: 1px solid #d8e0e8;
    border-radius: 0.875rem;
    background: #ffffff;
    overflow: hidden;
  }

  .einvoice-document-masthead {
    display: grid;
    grid-template-columns: minmax(0, 1.2fr) minmax(340px, 0.8fr);
    gap: 1.5rem;
    padding: 1.5rem;
    border-bottom: 1px solid #e2e8ef;
    background: #fbfcfe;
  }

  .einvoice-document-masthead__identity {
    flex: 1;
    min-width: 0;
  }

  .einvoice-document-kicker {
    margin-bottom: 0.45rem;
    color: #6b7a8c;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .einvoice-document-title {
    color: #1f2a37;
    font-size: 1.52rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.12;
  }

  .einvoice-document-subtitle {
    color: #607082;
    font-size: 0.95rem;
    line-height: 1.4;
  }

  .einvoice-document-lead-meta {
    display: grid;
    gap: 0.6rem;
    margin-top: 1rem;
    max-width: 32rem;
  }

  .einvoice-document-lead-row {
    display: grid;
    grid-template-columns: 124px minmax(0, 1fr);
    gap: 0.85rem;
    align-items: baseline;
    padding-top: 0.55rem;
    border-top: 1px dashed #e5ebf2;
  }

  .einvoice-document-lead-label {
    color: #7a8698;
    font-size: 0.79rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .einvoice-document-lead-value {
    color: #243140;
    font-size: 0.96rem;
    font-weight: 600;
    line-height: 1.4;
  }

  .einvoice-document-masthead__facts {
    display: grid;
    gap: 1rem;
    align-content: start;
    padding-left: 1.5rem;
    border-left: 1px solid #e5ebf2;
  }

  .einvoice-document-fact-group {
    padding-bottom: 0.95rem;
    border-bottom: 1px solid #e5ebf2;
  }

  .einvoice-document-fact-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.9rem 1rem;
  }

  .einvoice-document-fact-item--status {
    grid-column: span 2;
  }

  .einvoice-fact-label {
    margin-bottom: 0.38rem;
    color: #7a8797;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
  }

  .einvoice-fact-value {
    color: #243140;
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.35;
  }

  .einvoice-fact-value--invoice {
    font-size: 2rem;
    font-weight: 700;
    letter-spacing: -0.03em;
    line-height: 1;
  }

  .einvoice-document-parties {
    display: grid;
    // grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0;
    border-bottom: 1px solid #e1e7ef;
    background: #fbfcfe;
  }

  .einvoice-party-block {
    padding: 1.15rem 1.5rem 1.2rem;

    // &:first-child {
    //   border-right: 1px solid #e5ebf2;
    // }
  }

  .einvoice-party-heading {
    margin-bottom: 0.85rem;
  }

  .einvoice-party-label {
    margin-bottom: 0.25rem;
    color: #7b8797;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .einvoice-party-title {
    color: #1f2b38;
    font-size: 1.06rem;
    font-weight: 700;
    line-height: 1.35;
  }

  .einvoice-party-grid {
    display: grid;
    gap: 0.55rem;
  }

  .einvoice-party-row {
    display: grid;
    grid-template-columns: 132px minmax(0, 1fr);
    gap: 0.8rem;
    padding-top: 0.6rem;
    border-top: 1px dashed #e6ebf2;
  }

  .einvoice-party-row__label {
    color: #728094;
    font-size: 0.84rem;
    font-weight: 600;
    line-height: 1.35;
  }

  .einvoice-party-row__value {
    color: #243140;
    font-size: $einvoice-type-md;
    line-height: 1.45;
    min-width: 0;
  }

  .einvoice-document-body {
    padding: 1rem 1rem 1.15rem;
  }

  .einvoice-document-section-label {
    margin-bottom: 0.8rem;
    color: #6f7d8f;
    font-size: 0.74rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .einvoice-traceability-head__eyebrow {
    margin-bottom: 0.3rem;
    color: #8793a3;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .table th,
  .table td {
    vertical-align: middle;
  }

  .table-bordered-list th {
    border: 1px solid #dde4ec;
    background-color: #f6f8fb;
    color: #6f7d8f;
    font-size: 0.74rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  .table-bordered-list td {
    border: 1px solid #e4e9ef;
    color: #2e3948;
  }

  .table-hover tbody tr {
    transition: background-color 0.15s ease;

    &:hover {
      background-color: rgba(54, 153, 255, 0.06);
    }
  }

  .font-size-lg {
    font-size: $einvoice-type-xl;
  }

  @media (max-width: 1199.98px) {
    .einvoice-document-masthead {
      grid-template-columns: 1fr;
      gap: 1.1rem;
      padding: 1.35rem 1.35rem 1.2rem;
    }

    .einvoice-document-masthead__facts {
      padding-left: 0;
      border-left: 0;
      border-top: 1px solid #e5ebf2;
      padding-top: 1rem;
    }

    .einvoice-document-parties {
      grid-template-columns: 1fr;
    }

    .einvoice-party-block:first-child {
      border-right: 0;
      border-bottom: 1px solid #e6ebf2;
    }
  }

  .einvoice-adj-badge {
    font-size: 0.7rem;
    font-weight: 600;
    padding: 0.2em 0.5em;
    border-radius: 0.25rem;
    cursor: pointer;
    text-decoration: none;
    white-space: nowrap;
    vertical-align: middle;

    &:hover {
      opacity: 0.85;
      text-decoration: none;
    }
  }

  @media (max-width: 767.98px) {
    .einvoice-detail-document {
      border-radius: 0.8rem;
    }

    .einvoice-document-title {
      font-size: 1.28rem;
    }

    .einvoice-document-masthead,
    .einvoice-party-block,
    .einvoice-document-body {
      padding-left: 1rem;
      padding-right: 1rem;
    }

    .einvoice-document-lead-row,
    .einvoice-party-row {
      grid-template-columns: 1fr;
      gap: 0.22rem;
    }

    .einvoice-document-fact-grid {
      grid-template-columns: 1fr;
      gap: 0.8rem;
    }

    .einvoice-document-fact-item--status {
      grid-column: auto;
    }

    .einvoice-fact-value--invoice {
      font-size: 1.72rem;
    }
  }
}
</style>
